import { ClockCircleOutlined, PlusOutlined } from '@ant-design/icons';
import { Switch } from 'antd';
import React, { useRef } from 'react';
import { dayjs } from '@esg/shared';
import {
    UseFieldArrayReturn,
    Control,
    Controller,
    UseFormWatch,
    FieldArrayWithId
    , FieldValues
} from 'react-hook-form';

import { IWorkingTimeFrame } from '../WorkingTime';
import { TextCommon } from '../../typo';
import { FieldArray, TimePickerField } from '../../inputs';
import { IconTrash } from '../../icons';
import { AppButton } from '../../buttons';
import {
    Block,
    FlexBox,
} from '../../layouts';
import { translateCodes } from '../../../locales';
import { getDayOfWeekCode } from '../../../helper';
import { IWorkingTimeItem } from '../../../../business/organization/api/branch/getDetailBranch';
import { useTranslation } from 'react-i18next';

interface WorkingTimeFramesProps {
    control: Control;
    workingTime: FieldArrayWithId<IWorkingTimeItem, 'breakTimes', 'id'>;
    name: string;
    watch: UseFormWatch<FieldValues>;
    useAddBreakTime?: boolean;
    employeeId?: string;
    parentName?: string;
}

export const WorkingTimeFrames = (props: WorkingTimeFramesProps) => {
    const { control, workingTime, name, watch, useAddBreakTime = true, employeeId } = props;
    const breakTimeFieldRef = useRef<UseFieldArrayReturn>();
    const { t } = useTranslation();

    const isOff = watch(`${name}.isDayOff`);

    const startHour = dayjs(watch(`${name}.startTime`)).hour();
    const endHour = dayjs(watch(`${name}.endTime`)).hour();
    const duration = Math.round(endHour - startHour);
    const dayOfWeekTitleCode = getDayOfWeekCode(workingTime.dayOfWeekTitle);

    return (
        <>
            <Block className={`table-row ${useAddBreakTime ? '' : 'no-break'}`} display='grid'>
                <FlexBox gap={12}>
                    <Controller
                        control={control}
                        name={`${name}.isDayOff`}
                        render={({ field }) => <Switch {...field} checked={!field.value} onChange={(e) => field.onChange(!e)} />}
                    />
                    <TextCommon fontWeight='bold' translateCode={dayOfWeekTitleCode} />
                </FlexBox>
                <TimePickerField
                    name={`${name}.startTime`}
                    format='HH:mm'
                    disabled={isOff}

                />
                <TimePickerField
                    disabled={isOff}
                    name={`${name}.endTime`}
                    format='HH:mm'
                />
                <FlexBox gap={4}>
                    <ClockCircleOutlined />
                    <TextCommon>{duration}h</TextCommon>
                </FlexBox>
                {useAddBreakTime && (
                    <AppButton
                        disabled={isOff}
                        icon={<PlusOutlined />}
                        className='add-break'
                        onClick={() => {
                            breakTimeFieldRef.current?.append({
                                startTime: dayjs().startOf('day').add(10, 'hours').toISOString(),
                                endTime: dayjs().startOf('day').add(11, 'hours').toISOString(),
                                dayOfWeek: workingTime.dayOfWeek,
                                dayOfWeekTitle: workingTime.dayOfWeekTitle,
                                isFixed: true,
                                employeeId: employeeId,
                            } as IWorkingTimeFrame['workingTime']);
                        }

                        }
                    >
                        Add Break
                    </AppButton>
                )}
            </Block>

            <FieldArray
                name={`${name}.breakTimes`}
                isUseWrapper={false}
            >
                {(fieldArray) => {
                    const { fields, remove, append } = fieldArray;
                    breakTimeFieldRef.current = fieldArray;

                    return (
                        <FlexBox direction='column'>
                            {fields?.map((field, index) => {
                                const breakTimeStartHour = dayjs(watch(`${name}.breakTimes.${index}.startTime`)).hour();
                                const breakTimeEndHour = dayjs(watch(`${name}.breakTimes.${index}.endTime`)).hour();
                                const breakTimeDuration = Math.round(breakTimeEndHour - breakTimeStartHour);
                                return (
                                    <Block key={field.id} className='table-row' display='grid'>
                                        <TextCommon textAlign='center' translateCode={translateCodes.BREAK} />
                                        <TimePickerField
                                            name={`breakTimes.${index}.startTime`}
                                            format='HH:mm'

                                        />
                                        <TimePickerField
                                            name={`breakTimes.${index}.endTime`}
                                            format='HH:mm'
                                        />
                                        <FlexBox gap={4}>
                                            <ClockCircleOutlined />
                                            <TextCommon>{breakTimeDuration}h</TextCommon>
                                        </FlexBox>
                                        <IconTrash
                                            onClick={() => {
                                                remove(index);
                                            }}
                                            className='cursor-pointer'
                                        />
                                    </Block>
                                );
                            })}
                            <AppButton icon={<PlusOutlined />} onClick={() => {
                                append({
                                    startTime: dayjs().startOf('day').add(10, 'hours').toISOString(),
                                    endTime: dayjs().startOf('day').add(11, 'hours').toISOString(),
                                });
                            }}>{t(translateCodes.ADD_BREAK)}</AppButton>
                        </FlexBox>
                    );
                }}
            </FieldArray>
        </>
    );
};

